import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Button, Alert, Chip, Stack, Divider,
  TextField, CircularProgress, Dialog, DialogTitle, DialogContent,
  DialogActions, Table, TableHead, TableRow, TableCell, TableBody,
  TableContainer, IconButton, Tooltip, Select, MenuItem,
  FormControl, InputLabel, Stepper, Step, StepLabel,
  LinearProgress, Accordion, AccordionSummary, AccordionDetails,
  InputAdornment, Checkbox, FormControlLabel
} from '@mui/material';
import {
  CheckCircle, ErrorOutlined, Add, Delete, Edit, Cable,
  People, ReceiptLong, ExpandMore, Visibility,
  VisibilityOff, Refresh, PhoneForwarded, Description, Search,
  SelectAll, DeselectOutlined
} from '@mui/icons-material';
import api from '../../services/api';

// ── Import type cards ──────────────────────────────────────────────────────
const IMPORT_TYPES = [
  { key: 'rate_plans', label: 'Rate Plans', icon: <Description sx={{ fontSize: 36 }} />, desc: 'Import pricelists as rate plan headers' },
  { key: 'carriers',   label: 'Carriers / Trunks', icon: <PhoneForwarded sx={{ fontSize: 36 }} />, desc: 'Import active trunks as Edge carriers' },
  { key: 'buy_rates',  label: 'Buy Rates → Carrier', icon: <Description sx={{ fontSize: 36 }} />, desc: 'Import a trunk’s buy rates onto an existing carrier' },
  { key: 'sell_rates', label: 'Sell Rates → Rate Plan', icon: <Description sx={{ fontSize: 36 }} />, desc: 'Import a pricelist’s sell rates onto an existing rate plan' },
  { key: 'customers',  label: 'Customers', icon: <People sx={{ fontSize: 36 }} />, desc: 'Import accounts with portal logins' },
  { key: 'dids',       label: 'DIDs → Customers', icon: <PhoneForwarded sx={{ fontSize: 36 }} />, desc: 'Import DIDs and map each ASTPP customer to an Edge customer' },
  { key: 'cdrs',       label: 'CDR Records', icon: <ReceiptLong sx={{ fontSize: 36 }} />, desc: 'Import call records for billing' },
];

// ── Connection form dialog ─────────────────────────────────────────────────
const ConnectionDialog: React.FC<{ open: boolean; initial?: any; onClose: () => void; onSaved: () => void }> = ({ open, initial, onClose, onSaved }) => {
  const blank = { name:'', ssh_host:'', ssh_port:'22', ssh_user:'root', ssh_password:'', ssh_private_key:'', mysql_host:'127.0.0.1', mysql_port:'3306', mysql_user:'root', mysql_password:'', mysql_database:'astpp' };
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [authMode, setAuthMode] = useState<'password'|'key'>('password');

  useEffect(() => { if (open) { setForm(initial ? { ...blank, ...initial, ssh_password:'', ssh_private_key:'', mysql_password:'' } : blank); setError(''); } }, [open, initial]);

  const f = (k: string) => (e: any) => setForm(v => ({ ...v, [k]: e.target.value }));
  const save = async () => {
    setSaving(true); setError('');
    try { await api.post('/import/astpp/connections', { ...form, id: initial?.id, ssh_port: parseInt(form.ssh_port)||22, mysql_port: parseInt(form.mysql_port)||3306 }); onSaved(); onClose(); }
    catch (e: any) { setError(e.response?.data?.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{initial ? 'Edit' : 'Add'} ASTPP Connection</DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Stack spacing={2}>
          <TextField fullWidth size="small" label="Connection Name" value={form.name} onChange={f('name')} placeholder="e.g. ASTPP Production" />
          <Divider textAlign="left"><Typography variant="caption">SSH Settings</Typography></Divider>
          <Stack direction="row" spacing={2}>
            <TextField fullWidth size="small" label="SSH Host / IP" value={form.ssh_host} onChange={f('ssh_host')} />
            <TextField sx={{ width: 110 }} size="small" label="Port" type="number" value={form.ssh_port} onChange={f('ssh_port')} />
          </Stack>
          <TextField fullWidth size="small" label="SSH Username" value={form.ssh_user} onChange={f('ssh_user')} />
          <FormControl size="small" fullWidth>
            <InputLabel>Auth Method</InputLabel>
            <Select label="Auth Method" value={authMode} onChange={e => setAuthMode(e.target.value as any)}>
              <MenuItem value="password">Password</MenuItem>
              <MenuItem value="key">Private Key (PEM)</MenuItem>
            </Select>
          </FormControl>
          {authMode === 'password' ? (
            <TextField fullWidth size="small" label={initial ? 'SSH Password (blank = keep)' : 'SSH Password'}
              type={showPwd ? 'text' : 'password'} value={form.ssh_password} onChange={f('ssh_password')}
              InputProps={{ endAdornment: <InputAdornment position="end"><IconButton size="small" onClick={() => setShowPwd(v => !v)}>{showPwd ? <VisibilityOff fontSize="small"/> : <Visibility fontSize="small"/>}</IconButton></InputAdornment> }} />
          ) : (
            <TextField fullWidth size="small" label="Private Key (PEM)" multiline rows={4} value={form.ssh_private_key} onChange={f('ssh_private_key')} />
          )}
          <Divider textAlign="left"><Typography variant="caption">MySQL Settings</Typography></Divider>
          <Stack direction="row" spacing={2}>
            <TextField fullWidth size="small" label="MySQL Host" value={form.mysql_host} onChange={f('mysql_host')} />
            <TextField sx={{ width: 110 }} size="small" label="Port" type="number" value={form.mysql_port} onChange={f('mysql_port')} />
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField fullWidth size="small" label="MySQL User" value={form.mysql_user} onChange={f('mysql_user')} />
            <TextField fullWidth size="small" label={initial ? 'MySQL Password (blank = keep)' : 'MySQL Password'}
              type={showPwd ? 'text' : 'password'} value={form.mysql_password} onChange={f('mysql_password')} />
          </Stack>
          <TextField fullWidth size="small" label="Database Name" value={form.mysql_database} onChange={f('mysql_database')} />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={saving || !form.name || !form.ssh_host}>
          {saving && <CircularProgress size={16} sx={{ mr: 1 }} />} Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ── Rate plan mapper ───────────────────────────────────────────────────────
const CREATE_NEW = '__create_new__';

const RatePlanMapper: React.FC<{
  sourceNames: string[];
  ratePlans: any[];
  mapping: Record<string,string>;
  defaultPlanId: string;
  onChange: (m: Record<string,string>) => void;
  onDefaultChange: (id: string) => void;
  onRatePlansRefresh: () => void;
}> = ({ sourceNames, ratePlans, mapping, defaultPlanId, onChange, onDefaultChange, onRatePlansRefresh }) => {
  const [creating, setCreating] = useState<string|null>(null);
  const [createError, setCreateError] = useState('');

  const handleSelect = async (sourceName: string, value: string) => {
    if (value !== CREATE_NEW) { onChange({ ...mapping, [sourceName]: value }); return; }
    setCreating(sourceName); setCreateError('');
    try {
      const { data } = await api.post('/rates/plans', { name: sourceName, plan_type: 'retail', currency: 'USD', status: 'active' });
      const newId = data.id;
      await api.get(`/import/rate-plan-mappings?source_system=astpp`);
      onRatePlansRefresh();
      onChange({ ...mapping, [sourceName]: newId });
    } catch (e: any) {
      setCreateError(`Failed to create "${sourceName}": ` + (e.response?.data?.message || e.message));
    } finally { setCreating(null); }
  };

  return (
    <Box>
      {createError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setCreateError('')}>{createError}</Alert>}
      <FormControl size="small" fullWidth sx={{ mb: 2 }}>
        <InputLabel>Default Rate Plan (fallback)</InputLabel>
        <Select label="Default Rate Plan (fallback)" value={defaultPlanId} onChange={e => onDefaultChange(e.target.value)}>
          <MenuItem value=""><em>— Leave unassigned —</em></MenuItem>
          {ratePlans.map(rp => <MenuItem key={rp.id} value={rp.id}>{rp.name}</MenuItem>)}
        </Select>
      </FormControl>
      {sourceNames.length > 0 && (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead><TableRow>
              <TableCell sx={{ fontWeight:700 }}>ASTPP Pricelist</TableCell>
              <TableCell sx={{ fontWeight:700 }}>Edge Rate Plan</TableCell>
              <TableCell sx={{ fontWeight:700 }}>Status</TableCell>
            </TableRow></TableHead>
            <TableBody>
              {sourceNames.map(sn => {
                const isMapped = !!mapping[sn];
                const mappedPlan = ratePlans.find(rp => rp.id === mapping[sn]);
                return (
                  <TableRow key={sn}>
                    <TableCell sx={{ fontFamily:'monospace' }}>{sn}</TableCell>
                    <TableCell>
                      {creating === sn ? (
                        <Box sx={{ display:'flex', alignItems:'center', gap:1 }}>
                          <CircularProgress size={16} />
                          <Typography variant="caption">Creating "{sn}"...</Typography>
                        </Box>
                      ) : (
                        <Select size="small" fullWidth displayEmpty value={mapping[sn]||''}
                          onChange={e => handleSelect(sn, e.target.value)}>
                          <MenuItem value=""><em>— Use default —</em></MenuItem>
                          <MenuItem value={CREATE_NEW} sx={{ color:'primary.main', fontWeight:600 }}>
                            + Create new rate plan "{sn}"
                          </MenuItem>
                          <Divider />
                          {ratePlans.map(rp => <MenuItem key={rp.id} value={rp.id}>{rp.name}</MenuItem>)}
                        </Select>
                      )}
                    </TableCell>
                    <TableCell>
                      {creating === sn ? <Chip label="Creating..." size="small" color="info" /> :
                       isMapped ? <Chip label={mappedPlan ? `→ ${mappedPlan.name}` : 'Mapped'} size="small" color="success" /> :
                       <Chip label="Unassigned" size="small" color="warning" variant="outlined" />}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

// ── Main ASTPP tab ─────────────────────────────────────────────────────────
export const AstppDirectTab: React.FC = () => {
  const [connections, setConnections] = useState<any[]>([]);
  const [loadingConns, setLoadingConns] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editConn, setEditConn] = useState<any>(null);
  const [testing, setTesting] = useState<string|null>(null);
  const [selectedId, setSelectedId] = useState('');
  const [step, setStep] = useState(0);
  const [importType, setImportType] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [previewData, setPreviewData] = useState<any>(null);
  const [previewing, setPreviewing] = useState(false);
  const [ratePlans, setRatePlans] = useState<any[]>([]);
  const [ratePlanMap, setRatePlanMap] = useState<Record<string,string>>({});
  const [defaultPlanId, setDefaultPlanId] = useState('');
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState('');

  // ── Customer selection state ──────────────────────────────────────────────
  const [selectedCustomers, setSelectedCustomers] = useState<Set<string>>(new Set());
  const [customerSearch, setCustomerSearch] = useState('');
  // ── Buy-rates → existing carrier ──
  const [carriersList, setCarriersList] = useState<any[]>([]);
  const [buyRateTarget, setBuyRateTarget] = useState<Record<string,string>>({});
  const [buyImporting, setBuyImporting] = useState('');
  const [buyResults, setBuyResults] = useState<Record<string,any>>({});
  // ── Sell-rates → existing rate plan ──
  const [ratePlansList, setRatePlansList] = useState<any[]>([]);
  const [sellRateTarget, setSellRateTarget] = useState<Record<string,string>>({});
  const [sellImporting, setSellImporting] = useState('');
  const [sellResults, setSellResults] = useState<Record<string,any>>({});
  // ── DIDs → customers ──
  const [esbcTenants, setEsbcTenants] = useState<any[]>([]);
  const [didMap, setDidMap] = useState<Record<string,string>>({}); // astpp accountid -> esbc tenant_id
  const [didCarrier, setDidCarrier] = useState('');
  const [didImportFree, setDidImportFree] = useState(false);

  const loadConns = async () => {
    setLoadingConns(true);
    try { const { data } = await api.get('/import/astpp/connections'); setConnections(data); }
    catch { } finally { setLoadingConns(false); }
  };

  useEffect(() => {
    loadConns();
    api.get('/import/rate-plans').then(r => setRatePlans(r.data)).catch(() => {});
    const now = new Date(); const y = now.getFullYear(), m = now.getMonth();
    setDateFrom(new Date(y, m - 4, 1).toISOString().slice(0, 10));
    setDateTo(new Date(y, m + 1, 0).toISOString().slice(0, 10));
  }, []);

  const testConn = async (id: string) => {
    setTesting(id);
    try { await api.post(`/import/astpp/connections/${id}/test`); await loadConns(); }
    catch { await loadConns(); } finally { setTesting(null); }
  };

  const selectConnection = (id: string) => {
    setSelectedId(id); setStep(1); setImportType(''); setPreviewData(null); setResult(null);
    setError(''); setRatePlanMap({}); setSelectedCustomers(new Set()); setCustomerSearch('');
  };

  const doPreview = useCallback(async () => {
    if (!selectedId || !importType) return;
    setPreviewing(true); setError(''); setPreviewData(null);
    setSelectedCustomers(new Set()); setCustomerSearch('');
    try {
      let data: any;
      if (importType === 'customers') {
        // limit=0 → fetch all customers for selection
        ({ data } = await api.get(`/import/astpp/connections/${selectedId}/preview-customers?limit=0`));
        // pre-select all by default
        const allIds = new Set<string>((data.rows || []).map((r: any) => (r.number || '').trim()).filter(Boolean));
        setSelectedCustomers(allIds);
      } else if (importType === 'rate_plans') {
        ({ data } = await api.get(`/import/astpp/connections/${selectedId}/preview-rate-plans`));
      } else if (importType === 'carriers') {
        ({ data } = await api.get(`/import/astpp/connections/${selectedId}/preview-carriers`));
      } else if (importType === 'buy_rates') {
        // source trunks (with buy_rate_count) + the list of existing carriers to map onto
        ({ data } = await api.get(`/import/astpp/connections/${selectedId}/preview-carriers`));
        setBuyRateTarget({}); setBuyResults({});
        try { const cr = await api.get('/carriers?limit=200'); setCarriersList(cr.data?.data || cr.data || []); } catch { setCarriersList([]); }
      } else if (importType === 'sell_rates') {
        // source pricelists + the list of existing rate plans to map onto
        ({ data } = await api.get(`/import/astpp/connections/${selectedId}/preview-rate-plans`));
        setSellRateTarget({}); setSellResults({});
        try { const rp = await api.get('/rates/plans?limit=500'); setRatePlansList(rp.data?.data || rp.data || []); } catch { setRatePlansList([]); }
      } else if (importType === 'dids') {
        ({ data } = await api.get(`/import/astpp/connections/${selectedId}/preview-dids`));
        // load Edge customers (tenants, non-provider) + carriers to map onto
        let tenants: any[] = [];
        try { const tr = await api.get('/tenants?limit=200'); tenants = (tr.data?.data || tr.data || []).filter((t: any) => t.tier !== 'provider'); } catch { tenants = []; }
        setEsbcTenants(tenants);
        try { const cr = await api.get('/carriers?limit=200'); setCarriersList(cr.data?.data || cr.data || []); } catch { setCarriersList([]); }
        // Auto-match each ASTPP customer to an Edge tenant by (case-insensitive) name/code
        const norm = (s: string) => (s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
        const map: Record<string,string> = {};
        for (const c of (data.customers || [])) {
          if (c.accountid === '0') continue;
          const cn = norm(c.astpp_customer), cc = norm(c.acct_code);
          const hit = tenants.find((t: any) => { const tn = norm(t.name); return tn && (tn === cn || (cn && (tn.includes(cn) || cn.includes(tn))) || (cc && tn === cc)); });
          if (hit) map[c.accountid] = hit.id;
        }
        setDidMap(map); setDidCarrier(''); setDidImportFree(false);
      } else {
        ({ data } = await api.get(`/import/astpp/connections/${selectedId}/preview-cdrs?date_from=${dateFrom}&date_to=${dateTo}&limit=25`));
      }
      setPreviewData(data); setStep(2);
    } catch (e: any) { setError(e.response?.data?.message || 'Preview failed'); }
    finally { setPreviewing(false); }
  }, [selectedId, importType, dateFrom, dateTo]);

  useEffect(() => { if (importType && selectedId) doPreview(); }, [importType]);

  const doImport = async () => {
    setImporting(true); setError('');
    try {
      let data: any;
      if (importType === 'rate_plans') {
        ({ data } = await api.post('/import/astpp/import-rate-plans', { connection_id: selectedId }));
      } else if (importType === 'carriers') {
        ({ data } = await api.post('/import/astpp/import-carriers', { connection_id: selectedId }));
      } else if (importType === 'customers') {
        ({ data } = await api.post('/import/astpp/import-customers', {
          connection_id: selectedId,
          rate_plan_mapping: ratePlanMap,
          default_rate_plan_id: defaultPlanId || undefined,
          selected_ids: [...selectedCustomers],
        }));
      } else {
        ({ data } = await api.post('/import/astpp/import-cdrs', { connection_id: selectedId, date_from: dateFrom + ' 00:00:00', date_to: dateTo + ' 23:59:59' }));
      }
      setResult(data); setStep(3);
    } catch (e: any) { setError(e.response?.data?.message || 'Import failed'); }
    finally { setImporting(false); }
  };

  const doBuyRateImport = async (trunkId: string) => {
    const targetCarrierId = buyRateTarget[trunkId];
    if (!targetCarrierId) return;
    setBuyImporting(trunkId); setError('');
    try {
      const { data } = await api.post('/import/astpp/import-buy-rates', {
        connection_id: selectedId, source_trunk_id: trunkId, target_carrier_id: targetCarrierId,
      });
      setBuyResults(prev => ({ ...prev, [trunkId]: data }));
    } catch (e: any) { setError(e.response?.data?.message || 'Buy-rate import failed'); }
    finally { setBuyImporting(''); }
  };

  const doSellRateImport = async (pricelistId: string) => {
    const targetPlanId = sellRateTarget[pricelistId];
    if (!targetPlanId) return;
    setSellImporting(pricelistId); setError('');
    try {
      const { data } = await api.post('/import/astpp/import-sell-rates', {
        connection_id: selectedId, source_pricelist_id: pricelistId, target_rate_plan_id: targetPlanId,
      });
      setSellResults(prev => ({ ...prev, [pricelistId]: data }));
    } catch (e: any) { setError(e.response?.data?.message || 'Sell-rate import failed'); }
    finally { setSellImporting(''); }
  };

  const doDidImport = async () => {
    setImporting(true); setError('');
    try {
      const { data } = await api.post('/import/astpp/import-dids', {
        connection_id: selectedId,
        assignments: didMap,
        carrier_id: didCarrier || undefined,
        import_free: didImportFree,
      });
      setResult(data); setStep(3);
    } catch (e: any) { setError(e.response?.data?.message || 'DID import failed'); }
    finally { setImporting(false); }
  };

  const reset = () => {
    setStep(1); setImportType(''); setPreviewData(null); setResult(null);
    setError(''); setRatePlanMap({}); setSelectedCustomers(new Set()); setCustomerSearch('');
  };

  const sourcePlanNames = previewData?.rows ? [...new Set<string>(previewData.rows.map((r: any) => r.pricelist_name).filter(Boolean))] : [];
  const statusIcon = (c: any) => c.last_test_status === 'success' ? <Chip label="Connected" color="success" size="small" icon={<CheckCircle />} /> : c.last_test_status === 'failed' ? <Chip label="Failed" color="error" size="small" icon={<ErrorOutlined />} /> : <Chip label="Not tested" size="small" variant="outlined" />;

  const steps = ['Select Connection', 'Choose Import', 'Preview', 'Done'];

  // ── Customer selection helpers ────────────────────────────────────────────
  const allCustomerRows: any[] = previewData?.rows || [];
  const filteredCustomerRows = customerSearch.trim()
    ? allCustomerRows.filter((r: any) =>
        (r.name || '').toLowerCase().includes(customerSearch.toLowerCase()) ||
        (r.number || '').includes(customerSearch) ||
        (r.email || '').toLowerCase().includes(customerSearch.toLowerCase())
      )
    : allCustomerRows;
  const allFilteredIds = filteredCustomerRows.map((r: any) => (r.number || '').trim()).filter(Boolean);
  const allFilteredSelected = allFilteredIds.length > 0 && allFilteredIds.every((id: string) => selectedCustomers.has(id));
  const someFilteredSelected = allFilteredIds.some((id: string) => selectedCustomers.has(id));

  const toggleCustomer = (id: string) => {
    const next = new Set(selectedCustomers);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelectedCustomers(next);
  };

  const toggleAllFiltered = () => {
    const next = new Set(selectedCustomers);
    if (allFilteredSelected) {
      allFilteredIds.forEach((id: string) => next.delete(id));
    } else {
      allFilteredIds.forEach((id: string) => next.add(id));
    }
    setSelectedCustomers(next);
  };

  return (
    <Box>
      {/* ── Connections ── */}
      <Accordion defaultExpanded={connections.length === 0}>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography fontWeight={600}>ASTPP Connections ({connections.length})</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display:'flex', justifyContent:'flex-end', mb: 2 }}>
            <Button startIcon={<Add />} variant="outlined" size="small" onClick={() => { setEditConn(null); setDialogOpen(true); }}>Add Connection</Button>
          </Box>
          {loadingConns ? <CircularProgress size={24} /> : connections.length === 0 ? (
            <Alert severity="info">No connections yet. Add your ASTPP server above.</Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead><TableRow>
                  <TableCell sx={{ fontWeight:700 }}>Name</TableCell>
                  <TableCell sx={{ fontWeight:700 }}>SSH Host</TableCell>
                  <TableCell sx={{ fontWeight:700 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight:700 }}>Last Message</TableCell>
                  <TableCell />
                </TableRow></TableHead>
                <TableBody>
                  {connections.map(c => (
                    <TableRow key={c.id} selected={selectedId === c.id} sx={{ cursor:'pointer' }} onClick={() => selectConnection(c.id)}>
                      <TableCell fontWeight={600}>{c.name}</TableCell>
                      <TableCell sx={{ fontFamily:'monospace' }}>{c.ssh_host}:{c.ssh_port}</TableCell>
                      <TableCell>{statusIcon(c)}</TableCell>
                      <TableCell sx={{ fontSize:11, color:'text.secondary', maxWidth:300, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{c.last_test_message || '—'}</TableCell>
                      <TableCell onClick={e => e.stopPropagation()}>
                        <Stack direction="row" spacing={0.5}>
                          <Tooltip title="Test connection"><IconButton size="small" color="primary" disabled={testing === c.id} onClick={() => testConn(c.id)}>{testing === c.id ? <CircularProgress size={14}/> : <Cable fontSize="small"/>}</IconButton></Tooltip>
                          <Tooltip title="Edit"><IconButton size="small" onClick={() => { setEditConn(c); setDialogOpen(true); }}><Edit fontSize="small"/></IconButton></Tooltip>
                          <Tooltip title="Delete"><IconButton size="small" color="error" onClick={async () => { await api.delete(`/import/astpp/connections/${c.id}`); loadConns(); }}><Delete fontSize="small"/></IconButton></Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </AccordionDetails>
      </Accordion>

      {/* ── Wizard ── */}
      {selectedId && (
        <Box sx={{ mt: 3 }}>
          <Alert severity="info" sx={{ mb: 2 }}>Using: <strong>{connections.find(c => c.id === selectedId)?.name}</strong></Alert>
          <Stepper activeStep={step - 1} sx={{ mb: 3 }}>
            {steps.slice(1).map(s => <Step key={s}><StepLabel>{s}</StepLabel></Step>)}
          </Stepper>
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

          {/* Step 1: Choose import type */}
          {step === 1 && (
            <Paper variant="outlined" sx={{ p: 3 }}>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>What do you want to import from ASTPP?</Typography>
              <Box sx={{ display:'grid', gridTemplateColumns:'repeat(2,1fr)', gap: 2, mb: 3 }}>
                {IMPORT_TYPES.map(t => (
                  <Paper key={t.key} variant="outlined" onClick={() => setImportType(t.key)} sx={{
                    p: 2, cursor:'pointer', textAlign:'center',
                    borderColor: importType === t.key ? 'primary.main' : 'divider',
                    borderWidth: importType === t.key ? 2 : 1,
                    '&:hover': { borderColor:'primary.main' }
                  }}>
                    <Box sx={{ color: importType === t.key ? 'primary.main' : 'text.secondary' }}>{t.icon}</Box>
                    <Typography fontWeight={600}>{t.label}</Typography>
                    <Typography variant="caption" color="text.secondary">{t.desc}</Typography>
                  </Paper>
                ))}
              </Box>
              {importType === 'cdrs' && (
                <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                  <TextField fullWidth type="date" size="small" label="From" InputLabelProps={{ shrink:true }} value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
                  <TextField fullWidth type="date" size="small" label="To" InputLabelProps={{ shrink:true }} value={dateTo} onChange={e => setDateTo(e.target.value)} />
                </Stack>
              )}
              {importType && (
                <Button fullWidth variant="contained" onClick={doPreview} disabled={previewing} startIcon={previewing ? <CircularProgress size={16}/> : <Visibility />}>
                  Preview {IMPORT_TYPES.find(t => t.key === importType)?.label} from ASTPP
                </Button>
              )}
              {previewing && <LinearProgress sx={{ mt: 1 }} />}
            </Paper>
          )}

          {/* Step 2 (buy rates): map each source trunk onto an existing carrier */}
          {step === 2 && previewData && importType === 'buy_rates' && (
            <Box>
              <Alert severity="info" sx={{ mb: 2 }}>
                Pick a source trunk and the <strong>existing carrier</strong> to attach its buy rates to.
                Rates import with <code>carrier_id</code> set and Kamailio LCR is rebuilt — no duplicate carrier created.
              </Alert>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead><TableRow>
                    {['Source Trunk (ASTPP)', 'Buy Rates', 'Host', '→ Attach to Carrier', 'Action', 'Result'].map(h =>
                      <TableCell key={h} sx={{ fontWeight: 700 }}>{h}</TableCell>)}
                  </TableRow></TableHead>
                  <TableBody>
                    {(previewData.rows || []).map((r: any) => (
                      <TableRow key={r.id} hover>
                        <TableCell sx={{ fontWeight: 600 }}>{r.name}</TableCell>
                        <TableCell>{r.buy_rate_count || 0}</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{r.host || '—'}</TableCell>
                        <TableCell>
                          <Select size="small" displayEmpty sx={{ minWidth: 160 }}
                            value={buyRateTarget[r.id] || ''}
                            onChange={e => setBuyRateTarget(prev => ({ ...prev, [r.id]: e.target.value }))}>
                            <MenuItem value=""><em>— select carrier —</em></MenuItem>
                            {carriersList.map((c: any) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
                          </Select>
                        </TableCell>
                        <TableCell>
                          <Button size="small" variant="contained" disabled={!buyRateTarget[r.id] || buyImporting === r.id}
                            onClick={() => doBuyRateImport(r.id)}
                            startIcon={buyImporting === r.id ? <CircularProgress size={14} /> : <Description />}>
                            Import
                          </Button>
                        </TableCell>
                        <TableCell>
                          {buyResults[r.id] && <Chip size="small" color="success" icon={<CheckCircle />}
                            label={`${buyResults[r.id].rates_imported} rates → ${buyResults[r.id].carrier}`} />}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between' }}>
                <Button onClick={() => { setStep(1); setPreviewData(null); }}>Back</Button>
                <Button variant="outlined" onClick={reset}>Done</Button>
              </Box>
            </Box>
          )}

          {/* Step 2 (sell rates): map each source pricelist onto an existing rate plan */}
          {step === 2 && previewData && importType === 'sell_rates' && (
            <Box>
              <Alert severity="info" sx={{ mb: 2 }}>
                Pick a source pricelist and the <strong>existing rate plan</strong> to import its sell rates into
                (e.g. SKYTEL-SELL). Rates are read live at call time — no routing sync needed.
              </Alert>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead><TableRow>
                    {['Source Pricelist (ASTPP)', 'Rates', '→ Import into Rate Plan', 'Action', 'Result'].map(h =>
                      <TableCell key={h} sx={{ fontWeight: 700 }}>{h}</TableCell>)}
                  </TableRow></TableHead>
                  <TableBody>
                    {(previewData.rows || []).map((r: any) => (
                      <TableRow key={r.id} hover>
                        <TableCell sx={{ fontWeight: 600 }}>{r.name}</TableCell>
                        <TableCell>{r.rate_count || 0}</TableCell>
                        <TableCell>
                          <Select size="small" displayEmpty sx={{ minWidth: 200 }}
                            value={sellRateTarget[r.id] || ''}
                            onChange={e => setSellRateTarget(prev => ({ ...prev, [r.id]: e.target.value }))}>
                            <MenuItem value=""><em>— select rate plan —</em></MenuItem>
                            {ratePlansList.map((p: any) => <MenuItem key={p.id} value={p.id}>{p.name}{p.currency ? ` (${p.currency})` : ''}</MenuItem>)}
                          </Select>
                        </TableCell>
                        <TableCell>
                          <Button size="small" variant="contained" disabled={!sellRateTarget[r.id] || sellImporting === r.id}
                            onClick={() => doSellRateImport(r.id)}
                            startIcon={sellImporting === r.id ? <CircularProgress size={14} /> : <Description />}>
                            Import
                          </Button>
                        </TableCell>
                        <TableCell>
                          {sellResults[r.id] && <Chip size="small" color="success" icon={<CheckCircle />}
                            label={`${sellResults[r.id].rates_imported} rates → ${sellResults[r.id].rate_plan}`} />}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between' }}>
                <Button onClick={() => { setStep(1); setPreviewData(null); }}>Back</Button>
                <Button variant="outlined" onClick={reset}>Done</Button>
              </Box>
            </Box>
          )}

          {/* Step 2 (DIDs): map each ASTPP customer onto an Edge customer */}
          {step === 2 && previewData && importType === 'dids' && (() => {
            const custs = previewData.customers || [];
            const mappedCount = custs.filter((c: any) => c.accountid !== '0' && didMap[c.accountid]).reduce((s: number, c: any) => s + Number(c.count || 0), 0);
            const freeGroup = custs.find((c: any) => c.accountid === '0');
            return (
              <Box>
                <Alert severity="info" sx={{ mb: 2 }}>
                  <strong>{previewData.total}</strong> DIDs on ASTPP across <strong>{custs.filter((c: any) => c.accountid !== '0').length}</strong> customers.
                  The Edge customer is auto-matched by name — adjust any row. Imported DIDs start <strong>inactive (503 → old switch)</strong> until you Go Live on the DID Numbers page.
                </Alert>
                <Stack direction="row" spacing={2} sx={{ mb: 2 }} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Select size="small" displayEmpty value={didCarrier} onChange={e => setDidCarrier(e.target.value)} sx={{ minWidth: 220 }}>
                    <MenuItem value=""><em>— DID issuer / carrier (optional) —</em></MenuItem>
                    {carriersList.map((c: any) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
                  </Select>
                  <Chip label={`${mappedCount} DID(s) will be assigned`} color={mappedCount ? 'primary' : 'default'} size="small" />
                </Stack>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead><TableRow>
                      {['ASTPP Customer', 'DIDs', '→ Edge Customer'].map(h => <TableCell key={h} sx={{ fontWeight: 700 }}>{h}</TableCell>)}
                    </TableRow></TableHead>
                    <TableBody>
                      {custs.filter((c: any) => c.accountid !== '0').map((c: any) => (
                        <TableRow key={c.accountid} hover>
                          <TableCell sx={{ fontWeight: 600 }}>{c.astpp_customer}{c.acct_code && <span style={{ color: '#999', fontWeight: 400 }}> · {c.acct_code}</span>}</TableCell>
                          <TableCell>{c.count}</TableCell>
                          <TableCell>
                            <Select size="small" displayEmpty sx={{ minWidth: 200 }}
                              value={didMap[c.accountid] || ''}
                              onChange={e => setDidMap(prev => ({ ...prev, [c.accountid]: e.target.value }))}>
                              <MenuItem value=""><em>— skip (don't import) —</em></MenuItem>
                              {esbcTenants.map((t: any) => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
                            </Select>
                          </TableCell>
                        </TableRow>
                      ))}
                      {freeGroup && (
                        <TableRow hover sx={{ bgcolor: 'action.hover' }}>
                          <TableCell sx={{ fontStyle: 'italic' }}>{freeGroup.astpp_customer}</TableCell>
                          <TableCell>{freeGroup.count}</TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Select size="small" displayEmpty sx={{ minWidth: 200 }}
                                value={didMap['0'] || ''}
                                onChange={e => { const v = e.target.value; setDidMap(prev => ({ ...prev, ['0']: v })); if (v) setDidImportFree(true); }}>
                                <MenuItem value=""><em>— inventory (unassigned) —</em></MenuItem>
                                {esbcTenants.map((t: any) => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
                              </Select>
                              {!didMap['0'] && (
                                <Button size="small" variant={didImportFree ? 'contained' : 'outlined'} onClick={() => setDidImportFree(v => !v)}>
                                  {didImportFree ? 'Importing to inventory' : 'Import to inventory'}
                                </Button>
                              )}
                            </Stack>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between' }}>
                  <Button onClick={() => { setStep(1); setPreviewData(null); }}>Back</Button>
                  <Button variant="contained" onClick={doDidImport} disabled={importing || (mappedCount === 0 && !didImportFree && !didMap['0'])}
                    startIcon={importing ? <CircularProgress size={16} /> : <PhoneForwarded />}>
                    Import DIDs
                  </Button>
                </Box>
              </Box>
            );
          })()}

          {/* Step 2: Preview + optional mapping */}
          {step === 2 && previewData && importType !== 'buy_rates' && importType !== 'sell_rates' && importType !== 'dids' && (
            <Box>
              <Alert severity="success" sx={{ mb: 2 }}>
                Found <strong>{previewData.total?.toLocaleString()}</strong> {IMPORT_TYPES.find(t => t.key === importType)?.label} on ASTPP.
                {importType === 'customers' && (
                  <> &nbsp;
                    <strong style={{ color: selectedCustomers.size === 0 ? 'red' : 'inherit' }}>
                      {selectedCustomers.size} selected for import.
                    </strong>
                  </>
                )}
              </Alert>

              {/* ── Customer selection table ── */}
              {importType === 'customers' ? (
                <Box sx={{ mb: 3 }}>
                  {/* Toolbar */}
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }} flexWrap="wrap" useFlexGap>
                    <TextField
                      size="small"
                      placeholder="Search by name, account #, email…"
                      value={customerSearch}
                      onChange={e => setCustomerSearch(e.target.value)}
                      sx={{ flex: 1, minWidth: 220 }}
                      InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
                    />
                    <Button size="small" variant="outlined" startIcon={<SelectAll />}
                      onClick={() => setSelectedCustomers(new Set(allCustomerRows.map((r: any) => (r.number||'').trim()).filter(Boolean)))}>
                      Select All ({allCustomerRows.length})
                    </Button>
                    <Button size="small" variant="outlined" color="error" startIcon={<DeselectOutlined />}
                      onClick={() => setSelectedCustomers(new Set())}>
                      Deselect All
                    </Button>
                    <Chip
                      label={`${selectedCustomers.size} / ${allCustomerRows.length} selected`}
                      color={selectedCustomers.size === 0 ? 'error' : selectedCustomers.size === allCustomerRows.length ? 'success' : 'primary'}
                      size="small"
                    />
                  </Stack>

                  <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 420 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell padding="checkbox" sx={{ bgcolor: 'background.paper' }}>
                            <Checkbox
                              size="small"
                              checked={allFilteredSelected}
                              indeterminate={!allFilteredSelected && someFilteredSelected}
                              onChange={toggleAllFiltered}
                            />
                          </TableCell>
                          <TableCell sx={{ fontWeight:700, whiteSpace:'nowrap' }}>Account #</TableCell>
                          <TableCell sx={{ fontWeight:700 }}>Name</TableCell>
                          <TableCell sx={{ fontWeight:700 }}>Email</TableCell>
                          <TableCell sx={{ fontWeight:700 }}>Balance</TableCell>
                          <TableCell sx={{ fontWeight:700 }}>Credit Limit</TableCell>
                          <TableCell sx={{ fontWeight:700 }}>Rate Plan</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredCustomerRows.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={7} align="center" sx={{ color:'text.secondary', py: 3 }}>
                              No customers match your search.
                            </TableCell>
                          </TableRow>
                        ) : (
                          filteredCustomerRows.map((row: any, i: number) => {
                            const id = (row.number || '').trim();
                            const isSelected = selectedCustomers.has(id);
                            return (
                              <TableRow
                                key={i}
                                selected={isSelected}
                                hover
                                onClick={() => toggleCustomer(id)}
                                sx={{ cursor: 'pointer' }}
                              >
                                <TableCell padding="checkbox">
                                  <Checkbox size="small" checked={isSelected} onChange={() => toggleCustomer(id)} onClick={e => e.stopPropagation()} />
                                </TableCell>
                                <TableCell sx={{ fontFamily:'monospace', fontSize:12 }}>{row.number}</TableCell>
                                <TableCell sx={{ fontSize:12, maxWidth:180, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{row.name}</TableCell>
                                <TableCell sx={{ fontSize:12, maxWidth:180, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{row.email || '—'}</TableCell>
                                <TableCell sx={{ fontSize:12, fontFamily:'monospace' }}>{row.balance}</TableCell>
                                <TableCell sx={{ fontSize:12, fontFamily:'monospace' }}>{row.credit_limit}</TableCell>
                                <TableCell sx={{ fontSize:12 }}>{row.pricelist_name || '—'}</TableCell>
                              </TableRow>
                            );
                          })
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  {customerSearch && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display:'block' }}>
                      Showing {filteredCustomerRows.length} of {allCustomerRows.length} customers. Checkbox in header selects/deselects filtered results only.
                    </Typography>
                  )}
                </Box>
              ) : (
                /* Non-customer preview table */
                <TableContainer component={Paper} variant="outlined" sx={{ mb: 3, maxHeight: 300 }}>
                  <Table size="small" stickyHeader>
                    <TableHead><TableRow>
                      {previewData.headers?.map((h: string) => <TableCell key={h} sx={{ fontWeight:700, whiteSpace:'nowrap' }}>{h.replace(/_/g,' ')}</TableCell>)}
                    </TableRow></TableHead>
                    <TableBody>
                      {previewData.rows?.map((row: any, i: number) => (
                        <TableRow key={i}>
                          {previewData.headers?.map((h: string) => (
                            <TableCell key={h} sx={{ whiteSpace:'nowrap', maxWidth:180, overflow:'hidden', textOverflow:'ellipsis', fontSize:12 }}>
                              {row[h]}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {/* Rate plan mapping for customers */}
              {importType === 'customers' && (
                <Accordion sx={{ mb: 3 }}>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography fontWeight={600}>Rate Plan Mapping</Typography>
                    <Chip label={`${Object.values(ratePlanMap).filter(Boolean).length}/${sourcePlanNames.length} mapped`} size="small" sx={{ ml: 2 }} />
                  </AccordionSummary>
                  <AccordionDetails>
                    <Alert severity="info" sx={{ mb: 2 }}>
                      Import <strong>Rate Plans</strong> first (step above) so Edge plans are available to map here.
                    </Alert>
                    <RatePlanMapper sourceNames={sourcePlanNames} ratePlans={ratePlans} mapping={ratePlanMap} defaultPlanId={defaultPlanId} onChange={setRatePlanMap} onDefaultChange={setDefaultPlanId}
                      onRatePlansRefresh={() => api.get('/import/rate-plans').then(r => setRatePlans(r.data)).catch(() => {})} />
                  </AccordionDetails>
                </Accordion>
              )}

              <Stack direction="row" spacing={2} justifyContent="flex-end">
                <Button onClick={() => { setStep(1); setPreviewData(null); }}>Back</Button>
                <Button
                  variant="contained"
                  color="success"
                  onClick={doImport}
                  disabled={importing || (importType === 'customers' && selectedCustomers.size === 0)}
                  startIcon={importing ? <CircularProgress size={16}/> : <CheckCircle />}
                >
                  {importType === 'customers'
                    ? `Import ${selectedCustomers.size} Customer${selectedCustomers.size !== 1 ? 's' : ''}`
                    : `Import ${previewData.total?.toLocaleString()} ${IMPORT_TYPES.find(t => t.key === importType)?.label}`}
                </Button>
              </Stack>
              {importing && <LinearProgress sx={{ mt: 2 }} />}
            </Box>
          )}

          {/* Step 3: Result */}
          {step === 3 && result && (
            <Box sx={{ textAlign:'center', py: 4 }}>
              <CheckCircle sx={{ fontSize:64, color:'success.main', mb: 2 }} />
              <Typography variant="h5" fontWeight={700} gutterBottom>
                {IMPORT_TYPES.find(t => t.key === importType)?.label} Imported
              </Typography>
              <Stack direction="row" spacing={2} justifyContent="center" sx={{ mb: 3 }} flexWrap="wrap">
                {importType === 'dids' ? (
                  <>
                    <Chip label={`${result.created} DID(s) imported`} color="success" />
                    {result.assigned > 0 && <Chip label={`${result.assigned} assigned to customers`} color="primary" />}
                    {result.inventory > 0 && <Chip label={`${result.inventory} to inventory`} color="info" />}
                  </>
                ) : (
                  <Chip label={`${result.imported} imported`} color="success" />
                )}
                {result.rates_imported > 0 && <Chip label={`${result.rates_imported} sell rates loaded`} color="info" />}
                {result.termination_rates_imported > 0 && <Chip label={`${result.termination_rates_imported.toLocaleString()} buy rates loaded`} color="secondary" />}
                {result.skipped > 0 && <Chip label={`${result.skipped} skipped`} color="warning" />}
                {result.failed > 0 && <Chip label={`${result.failed} failed`} color="error" />}
              </Stack>

              {/* Credentials table for customer imports */}
              {importType === 'customers' && result.credentials?.length > 0 && (
                <Box sx={{ textAlign:'left', mb: 3 }}>
                  <Alert severity="info" sx={{ mb: 2 }}>Portal logins created. Save these — passwords cannot be recovered after closing.</Alert>
                  <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 300 }}>
                    <Table size="small">
                      <TableHead><TableRow>
                        <TableCell sx={{ fontWeight:700 }}>Customer</TableCell>
                        <TableCell sx={{ fontWeight:700 }}>ASTPP #</TableCell>
                        <TableCell sx={{ fontWeight:700 }}>Username</TableCell>
                        <TableCell sx={{ fontWeight:700 }}>Password</TableCell>
                        <TableCell sx={{ fontWeight:700 }}>Rate Plan</TableCell>
                      </TableRow></TableHead>
                      <TableBody>
                        {result.credentials.map((c: any, i: number) => (
                          <TableRow key={i}>
                            <TableCell>{c.name}</TableCell>
                            <TableCell sx={{ fontFamily:'monospace' }}>{c.code}</TableCell>
                            <TableCell sx={{ fontFamily:'monospace' }}>{c.username}</TableCell>
                            <TableCell sx={{ fontFamily:'monospace', fontWeight:700 }}>{c.password}</TableCell>
                            <TableCell>{c.rate_plan || '—'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              {result.errors?.length > 0 && (
                <Alert severity="warning" sx={{ mb: 2, textAlign:'left' }}>
                  {result.errors.map((e: any, i: number) => <Typography key={i} variant="caption" display="block">Row {e.row||i}: {e.error}</Typography>)}
                </Alert>
              )}

              {importType === 'rate_plans' && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  Rate plans imported with their destination rates.
                  {result.rates_imported > 0 && <strong> {result.rates_imported} rates loaded.</strong>}
                  {' '}Buy rates default to 0 — update in Rate Plans if needed.
                </Alert>
              )}
              {importType === 'carriers' && result.created?.length > 0 && (
                <Box sx={{ textAlign:'left', mb: 3 }}>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Imported Carriers & Termination Plans</Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead><TableRow>
                        <TableCell sx={{ fontWeight:700 }}>Carrier</TableCell>
                        <TableCell sx={{ fontWeight:700 }}>Termination Rate Plan</TableCell>
                        <TableCell sx={{ fontWeight:700 }}>Buy Rates</TableCell>
                      </TableRow></TableHead>
                      <TableBody>
                        {result.created.map((c: any, i: number) => (
                          <TableRow key={i}>
                            <TableCell>{c.name}</TableCell>
                            <TableCell sx={{ fontStyle:'italic', color:'text.secondary' }}>{c.termination_plan}</TableCell>
                            <TableCell><Chip label={c.buy_rates} size="small" color={c.buy_rates > 0 ? 'secondary' : 'default'} /></TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
              {importType === 'carriers' && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  Carriers imported with paired <strong>Termination Rate Plans</strong> (plan type: Wholesale).
                  {result.termination_rates_imported > 0 && <> <strong>{result.termination_rates_imported.toLocaleString()}</strong> buy rates loaded from carrier outbound routes.</>}
                  {' '}Each carrier has a matching "<em>Carrier Name — Termination</em>" rate plan in Rate Plans.
                </Alert>
              )}
              {importType === 'cdrs' && <Alert severity="info" sx={{ mb: 2 }}>CDRs imported. Go to Generate Invoices tab to create invoices.</Alert>}

              <Stack direction="row" spacing={2} justifyContent="center">
                <Button variant="outlined" onClick={reset}>Import More</Button>
              </Stack>
            </Box>
          )}
        </Box>
      )}

      <ConnectionDialog open={dialogOpen} initial={editConn} onClose={() => setDialogOpen(false)} onSaved={loadConns} />
    </Box>
  );
};
